import React, { useEffect, useRef, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import { closed } from "../../components/drawer/drawerSlice";
import { Typography } from "@progress/kendo-react-common";
import { RxCross1 } from "react-icons/rx";
import {
  createTemplate,
  getAllActiveTemplates,
  getAllTemplates,
  getTemplateByID,
  updateTemplate,
} from "./services/template.services";
import { clearTemplateDetails } from "./templateSlice";
import { STATUSARRAY } from "../../_contstants/common";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import FormRichTextField from "../../components/formFields/FormRichTextField";
import IconButton from "../../components/common/IconButton";

const CreateTemplate: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const TemplateDetail = useAppSelector(
    (state) => state.template.TemplateDetail,
  );
  const TemplateID = useAppSelector((state) => state.drawer.data);
  const formLoading = useAppSelector((state) => state.template.formLoading);
  const isIndirect = useAppSelector((state) => state.drawer.isIndirect);
  const FilterTemplate: any = useAppSelector(
    (state) => state.template.FilterTemplate,
  );

  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    if (TemplateID) {
      dispatch(getTemplateByID(TemplateID));
    }
  }, [TemplateID]);

  useEffect(() => {
    return () => {
      dispatch(clearTemplateDetails());
      dispatch(closed());
    };
  }, []);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [TemplateDetail]);

  const handleSubmit = async (values: any) => {
    try {
      if (!TemplateID) {
        const insertPayload = {
          TemplateName: values?.TemplateName ? values?.TemplateName : "",
          MetaApprovedTemplateName: values?.MetaApprovedTemplateName
            ? values?.MetaApprovedTemplateName
            : "",
          TemplateDescription: values?.TemplateDescription
            ? values?.TemplateDescription
            : "",
          LanguageCode: values?.LanguageCode ? values?.LanguageCode : "",
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(createTemplate(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearTemplateDetails());

          if (isIndirect) {
            dispatch(getAllActiveTemplates());
          } else {
            dispatch(getAllTemplates({ ...FilterTemplate }));
          }
        }
      } else {
        const editPayload = {
          ID: TemplateID,
          TemplateName: values?.TemplateName ? values?.TemplateName : "",
          MetaApprovedTemplateName: values?.MetaApprovedTemplateName
            ? values?.MetaApprovedTemplateName
            : "",
          TemplateDescription: values?.TemplateDescription
            ? values?.TemplateDescription
            : "",
          LanguageCode: values?.LanguageCode ? values?.LanguageCode : "",
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(updateTemplate(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearTemplateDetails());
          dispatch(getAllTemplates({ ...FilterTemplate }));
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
  };

  return (
    <>
      {formLoading && <LoadingPanel gridRef={gridRef} />}
      <Form
        key={formKey}
        onSubmit={handleSubmit}
        initialValues={TemplateDetail}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement style={{ width: "100%" }}>
            <GridLayout gap={{ rows: 10 }} cols={[{ width: "100%" }]}>
              <GridLayoutItem className="mui-drawer-header">
                <Typography.h4 className="m-0">
                  {TemplateID ? "Update Template" : "Add Template"}
                </Typography.h4>
                <IconButton
                  type="button"
                  fillMode={"flat"}
                  themeColor={"dark"}
                  style={{ marginRight: 4 }}
                  onClick={() => dispatch(closed())}
                  icon={RxCross1}
                />
              </GridLayoutItem>

              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  name="TemplateName"
                  label="Template Name"
                  placeholder="Template Name"
                  component={FormTextField}
                  validator={requiredValidator}
                  astrike={true}
                />
              </GridLayoutItem>
              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  name="MetaApprovedTemplateName"
                  label="Meta Approved Template Name"
                  placeholder="Meta Approved Template Name"
                  component={FormTextField}
                  validator={requiredValidator}
                  astrike={true}
                />
              </GridLayoutItem>

              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  name="TemplateDescription"
                  label="Template Description"
                  component={FormRichTextField}
                />
              </GridLayoutItem>
              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  name="LanguageCode"
                  label="Language Code"
                  placeholder="Language Code"
                  component={FormTextField}
                  validator={requiredValidator}
                  astrike={true}
                />
              </GridLayoutItem>
              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  name="IsActive"
                  label="Status"
                  component={FormSelectionField}
                  astrike={true}
                  validator={requiredValidator}
                  placeholder="i.e. Active"
                  options={STATUSARRAY?.map((status: any) => {
                    return {
                      value: status?.value,
                      label: status?.label,
                    };
                  })}
                />
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  textAlign: "end",
                  marginTop: "20px",
                  width: "100%",
                }}
              >
                <ButtonWithLoading
                  label={!TemplateID ? "Save" : "Update"}
                  type="submit"
                  disabled={!formRenderProps.allowSubmit || formLoading}
                  loading={formLoading}
                />

                <Button
                  type="button"
                  fillMode={"outline"}
                  themeColor={"primary"}
                  style={{ marginLeft: 10 }}
                  onClick={handleFormClose}
                >
                  Cancel
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </FormElement>
        )}
      />
    </>
  );
};

export default CreateTemplate;
